/**
 * Envio do programa completo da pós-graduação a partir do chat.
 *
 * Portado de `ModalBankSlip` (ramo `sendEmenta`): e-mail transacional
 * com a ementa anexada (`sendSyllabusEmail`) + evento Brevo `ementa_pos`.
 * Diferença de ordem em relação ao modal: aqui é chamado DEPOIS de o
 * negócio existir no Agendor, então uma falha do e-mail nunca invalida
 * o lead — quem chama decide como avisar o usuário.
 */

import { sendSyllabusEmail } from '@/actions/email/send-syllabus-email-action'
import { sendEventBrevoWithoutAuthAction } from '@/actions/send-event-brevo'
import { getSyllabusEmailErrorMessage } from '@/helpers/postgraduate/get-syllabus-email-error-message'

const WHITESPACE_PATTERN = /\s+/

const MISSING_COURSE_ERROR =
  'Curso inválido para envio do programa completo. Atualize a página e tente novamente.'

export interface SendProgramMaterialParams {
  readonly email: string
  readonly name: string
  readonly courseId?: string
  readonly courseName?: string
  readonly courseImage?: string | null
  /** Só dígitos com DDI, como vem do chat. Vai no evento Brevo. */
  readonly phone?: string
}

export type SendProgramMaterialResult =
  | { readonly success: true }
  | { readonly success: false; readonly error: string }

function getFirstName(name: string): string {
  return name.trim().split(WHITESPACE_PATTERN).at(0) ?? name
}

export async function sendProgramMaterial({
  email,
  name,
  courseId,
  courseName,
  courseImage,
  phone,
}: SendProgramMaterialParams): Promise<SendProgramMaterialResult> {
  if (!courseId) {
    return { success: false, error: MISSING_COURSE_ERROR }
  }

  try {
    const emailResult = await sendSyllabusEmail({
      email,
      name,
      firstName: getFirstName(name),
      productSegment: courseId,
      productName: courseName ?? '',
      productImage: courseImage ?? '',
    })

    const emailErrorMessage = getSyllabusEmailErrorMessage(emailResult)
    if (emailErrorMessage) {
      return { success: false, error: emailErrorMessage }
    }

    await sendEventBrevoWithoutAuthAction({
      event: 'ementa_pos',
      email,
      eventdata: {
        data: {
          course_id: courseId,
          course_name: courseName,
          email,
          nome: name,
          whatsapp: phone ?? '',
        },
      },
      properties: {
        course_name: courseName,
        course_id: courseId,
        email,
        nome: name,
        whatsapp: phone ?? '',
      },
    })

    return { success: true }
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : 'Não foi possível enviar o programa completo por e-mail.'
    return { success: false, error: message }
  }
}
